// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ChartCard
 * @package @geenius/analytics/solidjs
 * @description Renders the SolidJS chart card used to visualize simple
 * analytics series such as trends, counts, and comparisons. The component
 * converts shared chart points into a lightweight bar-style presentation.
 */

import type { ChartDataPoint } from "@geenius/analytics";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tooltip,
} from "@geenius/ui/solidjs";
import { type Component, For } from "solid-js";
import { cn } from "../lib/cn";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the SolidJS chart card.
 *
 * @property title Section heading displayed above the chart body.
 * @property data Ordered data points rendered as bars.
 * @property type Optional chart mode metadata for consumers.
 * @property height Optional pixel height for the chart body.
 * @property error Optional error value rendered instead of the chart body.
 * @property onRetry Optional recovery callback shown with the error state.
 * @property class Optional class name merged into the card wrapper.
 */
export interface ChartCardProps {
  title: string;
  data: ChartDataPoint[];
  type?: "bar" | "line";
  height?: number;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders a SolidJS chart card for a list of analytics datapoints.
 *
 * @param props Chart metadata and datapoints to visualize.
 * @returns A chart card element with proportional bars.
 */
export const ChartCard: Component<ChartCardProps> = (props) => {
  const max = (): number =>
    Math.max(...props.data.map((point) => point.value), 1);

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardHeader class="pb-0">
          <CardTitle class="text-sm">{props.title}</CardTitle>
        </CardHeader>
        <CardContent class="pt-4">
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card class={props.class}>
      <CardHeader class="pb-0">
        <CardTitle class="text-sm">{props.title}</CardTitle>
      </CardHeader>
      <CardContent class="pt-4">
        <div
          class="flex min-h-0 items-end gap-3"
          data-analytics="chart-body"
          data-chart-type={props.type ?? "bar"}
          style={{ height: `${props.height ?? 200}px` }}
        >
          <div
            class="flex h-full flex-1 items-end gap-3"
            data-analytics="chart-bars"
          >
            <For each={props.data}>
              {(point) => (
                <div
                  class="flex min-w-0 flex-1 flex-col items-center gap-2"
                  data-analytics="chart-bar-group"
                >
                  <Tooltip content={`${point.label}: ${point.value}`}>
                    <div
                      class={cn(
                        "w-full rounded-t-md bg-primary/80 transition-opacity",
                        props.type === "line" && "rounded-full bg-primary/60",
                      )}
                      data-analytics="chart-bar"
                      role="img"
                      aria-label={`${point.label}: ${point.value}`}
                      style={{ height: `${(point.value / max()) * 100}%` }}
                    />
                  </Tooltip>
                  <span
                    class="line-clamp-2 text-center text-xs text-muted-foreground"
                    data-analytics="chart-label"
                  >
                    {point.label}
                  </span>
                </div>
              )}
            </For>
          </div>
        </div>
        <ul class="sr-only" data-analytics="chart-data-list">
          <For each={props.data}>
            {(point) => (
              <li>
                {point.label}: {point.value}
              </li>
            )}
          </For>
        </ul>
      </CardContent>
    </Card>
  );
};
